import { jsPDF } from 'jspdf'
import { autoTable } from 'jspdf-autotable'
import Request from "../Request";

export interface TableToPdfOptions {
    columns: Array<string>;
    data: Array<Array<string | number>>;
    fileName: string;
    title?: string;
    subtitle?: string;
    orientation?: 'p' | 'l'; // portrait or landscape
    pageSize?: string; // 'a4', 'letter', etc.
}

class ReportService {
    static async getClassPerformanceReportData({ clientId, epochMonth = null }: { clientId: string|number, epochMonth: number | null}) {
        try {
            const params = epochMonth ? { epochMonth: epochMonth } : {};
            const response = await Request.get(`/api/clients/${clientId}/reports/class-performance`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Format a number as a percent (no decimals, with % sign)
     */
    static formatPercent(value: number | string): string {
        const num = Number(value);
        if (isNaN(num)) return '';
        return `${Math.round(num)}%`;
    }

    /**
     * Format a number as currency (no decimals, with $ and commas)
     */
    static formatCurrency(value: number | string): string {
        const num = Number(value);
        if (isNaN(num)) return '';
        return '$' + Math.round(num).toLocaleString();
    }

    static async tableToPdf({ 
        columns, 
        data, 
        fileName,
        title,
        subtitle,
        orientation = 'l',
        pageSize = 'a4'
    }: TableToPdfOptions) {
        try {
            const doc = new jsPDF(orientation, 'pt', pageSize);
            doc.setFontSize(20);
            const marginLeft = 20;
            const marginRight = 20;
            let startY = 40;
            if (title) {
                doc.text(title, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 30;
            }
            if (subtitle) {
                doc.setFontSize(14);
                doc.text(subtitle, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 30;
                doc.setFontSize(20);
            }
            autoTable(doc, {
                head: [columns],
                body: data,
                startY: startY,
                margin: { left: marginLeft, right: marginRight },
                styles: {
                    fontSize: 10,
                    cellPadding: 2, // reduced padding
                    overflow: 'linebreak', // only wrap at word boundaries
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [180, 180, 180],
                    cellWidth: 'auto',
                },
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [120, 120, 120],
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [180, 180, 180],
                },
                tableLineWidth: 0.5,
                tableLineColor: [120, 120, 120],
                didDrawPage: (data) => {
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
                        data.settings.margin.left,
                        pageHeight - 10
                    );
                    const date = new Date().toLocaleDateString();
                    doc.text(
                        `Generated on: ${date}`,
                        pageSize.width - 120,
                        pageHeight - 10
                    );
                }
            });
            const pdfUrl = doc.output("bloburl");
            window.open(pdfUrl, "_blank");
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
    
    // Add method for CSV export
    static tableToCSV({ columns, data, fileName }: { columns: Array<string>, data: Array<Array<string | number>>, fileName: string }) {
        try {
            // Create CSV content
            let csvContent = columns.join(',') + '\n';
            
            // Add data rows
            data.forEach(row => {
                // Properly handle commas and quotes in cell values
                const formattedRow = row.map(cell => {
                    const cellStr = String(cell);
                    // If cell contains comma, quote, or newline, wrap in quotes and escape any quotes
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                });
                csvContent += formattedRow.join(',') + '\n';
            });
            
            // Create a blob and download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${fileName}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    static calculateImu({ inflowRetail, inflowCost }: { inflowRetail: number, inflowCost: number }) {
        if (inflowRetail === 0) return 0;
        return (inflowRetail - inflowCost) / inflowRetail;
    }

    /**
     * Calculate mmu
     * 
     * @param inflowRetail Retail price of the inflow
     * @param inflowCost Cost price of the inflow
     * @param markdownPercent Markdown percentage to be applied (as decimal)
     */
    static calculateMmu({ inflowRetail, inflowCost, markdownPercent }: { inflowRetail: number, inflowCost: number, markdownPercent: number }) {
        const imu = ReportService.calculateImu({ inflowRetail, inflowCost });
        return imu - (markdownPercent * (1 - imu));
    }

    static calculateCop({ inflowCost, sales }: { inflowCost: number, sales: number }) {
        if (sales === 0) return 0;
        return inflowCost / sales;
    }
}

export default ReportService;